// src/App.js

import React, { useState } from 'react';
import ResultChart from './components/ResultChart';

// 금액(만원 단위)을 "X억 Y만원" 으로 포맷하는 함수
function formatKorean(amountInManwon) {
  const num = Math.round(Number(amountInManwon));
  const eok = Math.floor(num / 10000);
  const man = num % 10000;
  let str = '';
  if (eok) str += `${eok}억`;
  if (man) str += ` ${man}만원`;
  return str.trim() || '0만원';
}

function App() {
  // 상태 선언
  const [age, setAge] = useState('');
  const [salary, setSalary] = useState('');
  const [monthlyInvestRate, setMonthlyInvestRate] = useState('');
  const [currentSavings, setCurrentSavings] = useState('');
  const [annualReturn, setAnnualReturn] = useState('');
  const [years, setYears] = useState('');
  const [inflationRate, setInflationRate] = useState('');
  const [salaryGrowth, setSalaryGrowth] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [expenses, setExpenses] = useState([{ year: '', amount: '' }]);
  const [result, setResult] = useState(null);

  // 계산 함수
  const calculateFutureAssets = () => {
    const P = parseFloat(currentSavings) || 0;
    const investRate = parseFloat(monthlyInvestRate) / 100 || 0;
    const r = (parseFloat(annualReturn) / 100 || 0) / 12;
    const inflation = parseFloat(inflationRate) / 100 || 0;
    const salaryGrowthRate = parseFloat(salaryGrowth) / 100 || 0;
    const nYears = parseInt(years) || 0;
    const n = nYears * 12;

    let total = P;
    let currentMonthlySalary = (parseFloat(salary) || 0) / 12;
    const graphData = [];

    for (let month = 1; month <= n; month++) {
      total *= 1 + r;
      total += currentMonthlySalary * investRate;

      if (month % 12 === 0) {
        const year = month / 12;
        for (let i = 0; i < expenses.length; i++) {
          const e = expenses[i];
          if (e.year && e.amount && Number(e.year) === year) {
            total -= Number(e.amount);
            if (total < 0) total = 0;
          }
        }
        const realValue = total / Math.pow(1 + inflation, year);
        graphData.push({
          year,
          nominal: parseFloat(total.toFixed(2)),
          real: parseFloat(realValue.toFixed(2))
        });
        currentMonthlySalary *= 1 + salaryGrowthRate;
      }
    }

    const finalReal = total / Math.pow(1 + inflation, nYears);
    setResult({
      nominal: total.toFixed(2),
      real: finalReal.toFixed(2),
      graphData
    });
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #A7C7E7 0%, #FFFFFF 100%)',
      padding: '40px 20px',
      boxSizing: 'border-box'
    }}>
      <div style={{
        maxWidth: 600,
        margin: '0 auto',
        background: '#FFFFFF',
        borderRadius: 16,
        boxShadow: '0 8px 30px rgba(0,0,0,0.1)',
        padding: 30,
        fontFamily: 'Arial, sans-serif'
      }}>
        <h1 style={{ textAlign: 'center', marginBottom: 24 }}>돈워리 플랜:미래 자산 시뮬레이터</h1>

        {/* 입력폼 */}
        <label style={{ display: 'block', marginBottom: 16 }}>
          나이:
          <input
            type="number"
            value={age}
            onChange={e => setAge(e.target.value)}
            placeholder="현재 나이 입력"
            style={{ width: '100%', padding: 8, marginTop: 4, borderRadius: 6, border: '1px solid #ddd' }}
          />
        </label>
        <label style={{ display: 'block', marginBottom: 16 }}>
          연봉 (만원):
          <input
            type="number"
            value={salary}
            onChange={e => setSalary(e.target.value)}
            placeholder="예: 3000"
            style={{ width: '100%', padding: 8, marginTop: 4, borderRadius: 6, border: '1px solid #ddd' }}
          />
        </label>
        <label style={{ display: 'block', marginBottom: 16 }}>
          연봉 중 투자 비율 (%):
          <input
            type="number"
            value={monthlyInvestRate}
            onChange={e => setMonthlyInvestRate(e.target.value)}
            placeholder="예: 30"
            style={{ width: '100%', padding: 8, marginTop: 4, borderRadius: 6, border: '1px solid #ddd' }}
          />
        </label>
        <label style={{ display: 'block', marginBottom: 16 }}>
          현재 자산 (만원):
          <input
            type="number"
            value={currentSavings}
            onChange={e => setCurrentSavings(e.target.value)}
            placeholder="예: 1000"
            style={{ width: '100%', padding: 8, marginTop: 4, borderRadius: 6, border: '1px solid #ddd' }}
          />
        </label>
        <label style={{ display: 'block', marginBottom: 16 }}>
          예상 연 수익률 (%):
          <input
            type="number"
            value={annualReturn}
            onChange={e => setAnnualReturn(e.target.value)}
            placeholder="예: 7"
            style={{ width: '100%', padding: 8, marginTop: 4, borderRadius: 6, border: '1px solid #ddd' }}
          />
        </label>
        <label style={{ display: 'block', marginBottom: 16 }}>
          예측 기간 (년):
          <input
            type="number"
            value={years}
            onChange={e => setYears(e.target.value)}
            placeholder="예: 20"
            style={{ width: '100%', padding: 8, marginTop: 4, borderRadius: 6, border: '1px solid #ddd' }}
          />
        </label>

        {/* 고급 설정 */}
        <div style={{ textAlign: 'center', marginBottom: 16 }}>
          <button
            onClick={() => setShowAdvanced(!showAdvanced)}
            style={{
              background: '#F3F4F6',
              border: '1px solid #ccc',
              borderRadius: 6,
              padding: '8px 16px',
              cursor: 'pointer'
            }}
          >
            {showAdvanced ? '▲ 고급 설정 닫기' : '▼ 고급 설정 열기'}
          </button>
        </div>

        {showAdvanced && (
          <div style={{ background: '#FAFAFA', borderRadius: 10, padding: 20, marginBottom: 24 }}>
            <h4 style={{ marginBottom: 12 }}>큰돈 지출 입력 (집매매,결혼등)</h4>
            {expenses.map((e, i) => (
              <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                <input
                  type="number"
                  placeholder="연도(몇 년 후)"
                  value={e.year}
                  style={{ width: 100, padding: 6, borderRadius: 6, border: '1px solid #ddd' }}
                  onChange={ev => {
                    const newArr = [...expenses];
                    newArr[i].year = ev.target.value;
                    setExpenses(newArr);
                  }}
                />
                <input
                  type="number"
                  placeholder="금액(만원)"
                  value={e.amount}
                  style={{ width: 120, padding: 6, borderRadius: 6, border: '1px solid #ddd' }}
                  onChange={ev => {
                    const newArr = [...expenses];
                    newArr[i].amount = ev.target.value;
                    setExpenses(newArr);
                  }}
                />
                <button
                  onClick={() => setExpenses(expenses.filter((_, idx) => idx !== i))}
                  style={{ color: 'red', background: 'none', border: 'none', cursor: 'pointer' }}
                >
                  ❌
                </button>
              </div>
            ))}
            <button
              onClick={() => setExpenses([...expenses, { year: '', amount: '' }])}
              style={{ padding: '6px 12px', borderRadius: 6, background: '#E5E7EB', border: 'none', cursor: 'pointer' }}
            >
              + 지출 추가
            </button>
            <hr style={{ margin: '20px 0' }} />
            <label style={{ display: 'block', marginBottom: 12 }}>
              연봉 증가율 (%):
              <input
                type="number"
                value={salaryGrowth}
                onChange={e => setSalaryGrowth(e.target.value)}
                placeholder="예: 3"
                style={{ width: 80, padding: 6, borderRadius: 6, border: '1px solid #ddd', marginLeft: 8 }}
              />
            </label>
            <label style={{ display: 'block' }}>
              물가 상승률 (%):
              <input
                type="number"
                value={inflationRate}
                onChange={e => setInflationRate(e.target.value)}
                placeholder="예: 2"
                style={{ width: 80, padding: 6, borderRadius: 6, border: '1px solid #ddd', marginLeft: 8 }}
              />
            </label>
          </div>
        )}

        {/* 실행 버튼 */}
        <button
          onClick={calculateFutureAssets}
          style={{
            width: '100%',
            padding: '10px',
            background: '#4CAF50',
            color: 'white',
            fontWeight: 'bold',
            border: 'none',
            borderRadius: 6,
            cursor: 'pointer'
          }}
        >
          시뮬레이션 실행
        </button>

        {/* 결과 영역 */}
        {result && (
          <div style={{ marginTop: 24 }}>
            <h3>시뮬레이션 결과</h3>
            <p>미래 자산(명목): {formatKorean(result.nominal)}</p>
            <p>미래 자산(실질): {formatKorean(result.real)}</p>
            <ResultChart data={result.graphData} />
          </div>
        )}
      </div>
    </div>
  );
}

export default App;
